// Pure event router for OpenAI Realtime data-channel events.
//
// Splitting this out keeps the hook's React/WebRTC plumbing separate from
// the protocol mapping. The router is a switch on the event `type` that
// produces a discriminated `VoiceAction` the hook then applies. This makes
// the protocol surface easy to unit-test against canned event payloads.

export type VoiceStatus = 'listening' | 'speaking' | 'thinking';

export type VoiceAction =
  | { kind: 'status'; status: VoiceStatus }
  | { kind: 'user-transcript'; text: string }
  | { kind: 'assistant-delta'; delta: string }
  | { kind: 'assistant-final'; text: string }
  | {
      kind: 'tool-call';
      callId: string;
      name: string;
      argumentsJson: string;
      // FRONT-1469: which model turn requested this call, when the event
      // carried one — lets the turn gate scope reply-coalescing to that
      // turn instead of blocking on every outstanding tool call globally.
      responseId?: string;
    }
  | { kind: 'error'; message: string }
  | { kind: 'ignore' };

interface RealtimeEvent {
  type?: string;
  // Loosely typed — OpenAI emits many shapes; we pluck only what we need.
  transcript?: string;
  delta?: string;
  text?: string;
  name?: string;
  arguments?: string;
  call_id?: string;
  response_id?: string;
  error?: { message?: string } | string;
  message?: string;
}

export function routeVoiceEvent(raw: unknown): VoiceAction {
  if (!raw || typeof raw !== 'object') return { kind: 'ignore' };
  const e = raw as RealtimeEvent;
  const type = e.type ?? '';

  switch (type) {
    case 'input_audio_buffer.speech_started':
      return { kind: 'status', status: 'listening' };

    case 'input_audio_buffer.speech_stopped':
      return { kind: 'status', status: 'thinking' };

    case 'response.created':
      return { kind: 'status', status: 'thinking' };

    case 'response.output_audio.started':
    case 'response.output_audio_transcript.started':
      return { kind: 'status', status: 'speaking' };

    case 'conversation.item.input_audio_transcription.completed': {
      const text = (e.transcript ?? '').trim();
      if (!text) return { kind: 'ignore' };
      return { kind: 'user-transcript', text };
    }

    case 'response.output_audio_transcript.delta': {
      const delta = e.delta ?? '';
      if (!delta) return { kind: 'ignore' };
      return { kind: 'assistant-delta', delta };
    }

    case 'response.output_audio_transcript.done': {
      const text = (e.transcript ?? e.text ?? '').trim();
      return { kind: 'assistant-final', text };
    }

    // Tool calls. Two event shapes are emitted in practice — the GA event
    // is `response.function_call_arguments.done`, but older preview models
    // surfaced `response.output_item.done` for completed function items.
    // Handle the canonical one; treat unknown variants as ignorable.
    case 'response.function_call_arguments.done': {
      const callId = (e.call_id ?? '').trim();
      const name = (e.name ?? '').trim();
      if (!callId || !name) return { kind: 'ignore' };
      return {
        kind: 'tool-call',
        callId,
        name,
        argumentsJson: e.arguments ?? '{}',
        responseId: e.response_id,
      };
    }

    case 'error': {
      const msg =
        typeof e.error === 'string'
          ? e.error
          : (e.error?.message ?? e.message ?? 'Unknown realtime error');
      return { kind: 'error', message: msg };
    }

    default:
      return { kind: 'ignore' };
  }
}
